import {
    useState, useEffect, useRef, useCallback
} from 'react';
import {
    createChannel, updateChannel, fetchWhatsappQr, fetchWhatsappStatus, fetchWhatsappPairingCode, disconnectWhatsapp
} from '../configApi';
import {
    NewCombobox
} from 'shared/components/ui/combobox';
import {
    ChannelSetupLayout
} from 'builders/builderChannel/components/ChannelSetupLayout';
import BaileysConnectionPanel from 'builders/builderChannel/components/WhatsApp/BaileysConnectionPanel';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
    Card, CardContent, CardHeader
} from 'shared/components/ui/card';
import {
    CheckCircle2, ChevronLeft, Copy, Check, Pencil, MessageSquare, MessageCircle, Power, Eye
} from 'lucide-react';
import WebchatWidget from 'builders/builderChannel/components/WebchatConfig/WebchatWidget';
import {
    cn
} from 'shared/lib/utils';

const FEO_URL = process.env.REACT_APP_FEO_SERVER_URL || '';

const CHANNEL_TYPES = [
    // { id: 'Email',       label: 'Email',       description: 'Connect via Outlook',            icon: Mail,          iconBg: 'bg-indigo-600'  },
    {
        id: 'Webchat',
        label: 'Web Chat',
        description: 'Embed chat on your site',
        icon: MessageSquare,
        iconBg: 'bg-violet-600'
    },
    {
        id: 'Whatsapp',
        label: 'WhatsApp',
        description: 'Connect via WhatsApp Business',
        icon: MessageCircle,
        iconBg: 'bg-green-600'
    },
    // { id: 'Messenger',   label: 'Messenger',   description: 'Connect via Facebook Messenger', icon: Send,          iconBg: 'bg-blue-600'    },
    // { id: 'Voice',       label: 'Voice',       description: 'Voice channel integration',      icon: Mic,           iconBg: 'bg-orange-500'  },
];

// Callback URLs are ORG-SCOPED. FEO rejects the org-less form with a 400 (#427, and
// #407 before it): without :organizationId the channel lookup runs with no tenant
// filter and can resolve another org's — or another environment's — channel.
// Voice is /receiveCall, not /Receive — the old map named a route that does not exist.
const CALLBACK_PATHS = {
    Whatsapp:  'whatsapp/Receive',
    Messenger: 'messenger/Receive',
    Voice:     'voice/receiveCall',
};

const callbackUrlFor = (type, organizationId) => {
    const path = CALLBACK_PATHS[type];
    if (!path) return undefined;
    return organizationId
        ? `${FEO_URL}/feo-v3/api/${path}/${organizationId}`
        : undefined;
};

const TYPE_BADGE = {
    Email:       'bg-indigo-100 text-indigo-700',
    Whatsapp:    'bg-green-100 text-green-700',
    Messenger:   'bg-blue-100 text-blue-700',
    Voice:       'bg-purple-100 text-purple-700',
    Webchat:     'bg-cyan-100 text-cyan-700',
};

function getEditableFields(type) {
    switch (type) {
        case 'Email': return ['address', 'topic'];
        case 'Whatsapp': return ['phone', 'topic', 'callbackUrl'];
        case 'Messenger': return ['phone', 'topic', 'callbackUrl'];
        case 'Voice': return ['address', 'topic'];
        case 'Webchat': return ['webchatAddress', 'topic', 'origins', 'includeConvoHistory', 'webChatConfig'];
        default: return ['address', 'topic'];
    }
}

function getAddressPrefix(type) {
    if (type === 'Whatsapp') return 'whatsapp:+';
    if (type === 'Messenger') return 'messenger:+';
    return '';
}

function buildPayload(data, type) {
    const fields = getEditableFields(type);
    const prefix = getAddressPrefix(type);
    const address = fields.includes('phone') ? prefix + data.phone.replace(/^\+/, '') : data.address;
    const payload = {
        address,
        topic: data.topic,
        type
    };
    if (fields.includes('origins')) payload.origins = data.origins.split(',').map(s => s.trim()).filter(Boolean);
    if (fields.includes('includeConvoHistory')) payload.includeConvoHistory = data.includeConvoHistory;
    if (fields.includes('webChatConfig')) payload.webChatConfig = {
        assistantName:        data.webChatConfig.assistantName,
        greetingMessage:      data.webChatConfig.greetingMessage,
        accentColor:          data.webChatConfig.accentColor,
        primaryColor:         data.webChatConfig.primaryColor,
        conversationStarters: data.webChatConfig.conversationStarters.split(',').map(s => s.trim()).filter(Boolean),
    };
    return payload;
}

function emptyFormData(type) {
    return {
        phone: '',
        address: type === 'Webchat' ? crypto.randomUUID() : '',
        topic: '',
        origins: '',
        includeConvoHistory: false,
        webChatConfig: {
            assistantName: '',
            greetingMessage: '',
            accentColor: '',
            primaryColor: '',
            conversationStarters: ''
        },
    };
}

function fromChannel(ch) {
    const prefix = getAddressPrefix(ch.type);
    const phone = prefix && (ch.address || '').startsWith(prefix) ? (ch.address || '').slice(prefix.length) : '';
    return {
        phone,
        address: ch.address || '',
        topic: ch.topic || '',
        origins: (ch.origins || []).join(', '),
        includeConvoHistory: ch.includeConvoHistory || false,
        webChatConfig: {
            assistantName:        ch.webChatConfig?.assistantName || '',
            greetingMessage:      ch.webChatConfig?.greetingMessage || '',
            accentColor:          ch.webChatConfig?.accentColor || '',
            primaryColor:         ch.webChatConfig?.primaryColor || '',
            conversationStarters: (ch.webChatConfig?.conversationStarters || []).join(', '),
        },
    };
}

function ChannelForm({
    type, initialData, onSave, onCancel, saveLabel = 'Save', cancelLabel = 'Cancel', organizationId
}) {
    const [data, setData] = useState(initialData || emptyFormData(type));
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    const fields = getEditableFields(type);
    const set = (k, v) => setData(p => ({
        ...p,
        [k]: v
    }));
    const setWc = (k, v) => setData(p => ({
        ...p,
        webChatConfig: {
            ...p.webChatConfig,
            [k]: v
        }
    }));

    const handleCopy = (text) => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
    const handleSave = async () => {
        setSaving(true); setError(null);
        try { await onSave(buildPayload(data, type)); }
        catch (e) { setError(e.message); setSaving(false); }
    };

    const callbackUrl = callbackUrlFor(type, organizationId);

    return (
        <div className="space-y-4">
            {fields.includes('phone') && (
                <div className="space-y-1.5">
                    <Label>{type === 'Whatsapp' ? 'WhatsApp Number' : 'Messenger Number'}</Label>
                    <div className="flex rounded-md border border-input overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                        <span className="px-3 flex items-center text-sm text-muted-foreground bg-muted border-r whitespace-nowrap">
                            {type === 'Whatsapp' ? 'whatsapp:+' : 'messenger:+'}
                        </span>
                        <input className="flex-1 px-3 py-2 text-sm bg-transparent outline-none" value={data.phone} placeholder="1234567890" onChange={e => set('phone', e.target.value.replace(/\D/g, ''))} />
                    </div>
                </div>
            )}
            {fields.includes('address') && (
                <div className="space-y-1.5">
                    <Label>Address</Label>
                    <Input value={data.address} placeholder="Number:+1234567890" onChange={e => set('address', e.target.value)} />
                </div>
            )}
            {fields.includes('webchatAddress') && (
                <div className="space-y-1.5">
                    <Label>Widget ID <span className="text-muted-foreground font-normal text-xs">(use in your embed script)</span></Label>
                    <div className="flex gap-2">
                        <Input readOnly value={data.address} className="bg-muted text-muted-foreground font-mono text-xs" />
                        <Button variant="outline" size="icon" onClick={() => handleCopy(data.address)}>
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>
            )}
            {fields.includes('topic') && (
                <div className="space-y-1.5">
                    <Label>Topic</Label>
                    <Input value={data.topic} placeholder="Customer Support, Sales, etc." onChange={e => set('topic', e.target.value)} />
                </div>
            )}
            {fields.includes('callbackUrl') && callbackUrl && (
                <div className="space-y-1.5">
                    <Label>Callback URL <span className="text-muted-foreground font-normal text-xs">(copy and paste in Twilio)</span></Label>
                    <div className="flex gap-2">
                        <Input readOnly value={callbackUrl} className="bg-muted text-muted-foreground font-mono text-xs" />
                        <Button variant="outline" size="icon" onClick={() => handleCopy(callbackUrl)}>
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>
            )}
            {fields.includes('origins') && (
                <div className="space-y-1.5">
                    <Label>Allowed Domains</Label>
                    <Input value={data.origins} placeholder="https://example.com, https://app.example.com" onChange={e => set('origins', e.target.value)} />
                    <p className="text-xs text-muted-foreground">Comma-separated list of allowed origins</p>
                </div>
            )}
            {fields.includes('includeConvoHistory') && (
                <div className="flex items-center gap-2">
                    <input type="checkbox" id="convo-hist" checked={data.includeConvoHistory} onChange={e => set('includeConvoHistory', e.target.checked)} className="h-4 w-4" />
                    <Label htmlFor="convo-hist">Include Conversation History</Label>
                </div>
            )}
            {fields.includes('webChatConfig') && (
                <div className="space-y-3 rounded-lg border p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Widget Settings</p>
                    <div className="space-y-1.5">
                        <Label>Assistant Name</Label>
                        <Input value={data.webChatConfig.assistantName} onChange={e => setWc('assistantName', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Greeting Message</Label>
                        <Input value={data.webChatConfig.greetingMessage} onChange={e => setWc('greetingMessage', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Accent Color</Label>
                            <div className="flex gap-2">
                                <input type="color" value={data.webChatConfig.accentColor || '#3a6bff'} onChange={e => setWc('accentColor', e.target.value)} className="h-9 w-10 rounded border p-0.5 cursor-pointer" />
                                <Input value={data.webChatConfig.accentColor} placeholder="#3a6bff" onChange={e => setWc('accentColor', e.target.value)} />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Primary Color</Label>
                            <div className="flex gap-2">
                                <input type="color" value={data.webChatConfig.primaryColor || '#ffffff'} onChange={e => setWc('primaryColor', e.target.value)} className="h-9 w-10 rounded border p-0.5 cursor-pointer" />
                                <Input value={data.webChatConfig.primaryColor} placeholder="#ffffff" onChange={e => setWc('primaryColor', e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Conversation Starters</Label>
                        <Input value={data.webChatConfig.conversationStarters} placeholder="Hello, How can I help?, Tell me more" onChange={e => setWc('conversationStarters', e.target.value)} />
                        <p className="text-xs text-muted-foreground">Comma-separated list</p>
                    </div>
                </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2 pt-1 justify-end">
                <Button variant="outline" onClick={onCancel}>{cancelLabel}</Button>
                <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : saveLabel}</Button>
            </div>
        </div>
    );
}

const WA_MODE_HINT = {
    conversation: 'Assistant handles and replies to messages.',
    receive_only: 'Assistant processes messages silently — no reply sent.',
    external: 'Exposes an API to send messages from your own integration.',
};

// Numbered step row for the WhatsApp config sidebar — mirrors the builder's
// "Configure WhatsApp Instance" channel adder (WhatsAppConfigSidebar).
function WaStep({
    num, connector = true, children
}) {
    return (
        <div className="relative flex gap-3.5">
            <div className="flex flex-col items-center shrink-0">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[9px] font-bold z-10 shrink-0">
                    {num}
                </div>
                {connector && <div className="w-px flex-1 bg-border/60 mt-1 min-h-[12px]" />}
            </div>
            <div className={cn('flex-grow', connector ? 'pb-5' : 'pb-0')}>{children}</div>
        </div>
    );
}

const SIDEBAR_LABEL = 'text-[11px] font-semibold text-muted-foreground block mb-1.5';

function WhatsAppQrFlow({
    instanceId, configKey, assistantId, collections = [], onChannelCreated, onBack, onCancel
}) {
    // BaileysConnectionPanel vocabulary: '' (how-to) | pending | pairing | saving | connected | save_failed
    const [connectionStatus, setConnectionStatus] = useState('');
    const [topic, setTopic] = useState('');
    const [mode, setMode] = useState('conversation');
    const [method, setMethod] = useState('qr'); // qr | phone
    const [phone, setPhone] = useState('');
    const [collectionId, setCollectionId] = useState('');
    const [qrCode, setQrCode] = useState('');
    const [pairingCode, setPairingCode] = useState('');
    const [saveError, setSaveError] = useState('');

    const sessionIdRef = useRef(crypto.randomUUID());
    const statusIntervalRef = useRef(null);
    const mountedRef = useRef(true);
    const savingRef = useRef(false);

    const cleanup = useCallback(() => {
        if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; cleanup(); };
    }, [cleanup]);

    // Auto-save the channel once the WhatsApp account is linked. Topic/mode/
    // collection were collected in the sidebar, so no extra step is needed —
    // mirrors the builder WhatsAppSetup flow. `external` mode carries no
    // assistant (the backend mints an API key instead).
    const saveChannel = useCallback(async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        cleanup();
        setConnectionStatus('saving');
        try {
            const channel = await createChannel(instanceId, configKey, {
                type: 'Whatsapp',
                address: sessionIdRef.current,
                topic,
                assistantId: mode === 'external' ? '' : assistantId,
                mode,
                ...(mode !== 'external' && collectionId ? {
                    collectionId
                } : {}),
            });
            if (!mountedRef.current) return;
            setConnectionStatus('connected');
            onChannelCreated(channel);
        } catch (e) {
            if (!mountedRef.current) return;
            savingRef.current = false;
            setSaveError(e.message || 'Failed to save channel');
            setConnectionStatus('save_failed');
        }
    }, [instanceId, configKey, topic, mode, assistantId, collectionId, onChannelCreated, cleanup]);

    // The bridge surfaces the (rotating) QR through the /status response, so we
    // read `qr` from status polling rather than re-hammering /qr. On connected,
    // auto-save.
    const pollStatus = useCallback(async () => {
        try {
            const data = await fetchWhatsappStatus(instanceId, configKey, sessionIdRef.current);
            if (!mountedRef.current) return;
            const qr = typeof data === 'string' ? null : data?.qr;
            if (qr) setQrCode(qr);
            if (data?.status === 'connected') saveChannel();
        } catch {
            // ignore polling errors
        }
    }, [instanceId, configKey, saveChannel]);

    const startQr = useCallback(async () => {
        cleanup();
        setSaveError('');
        setConnectionStatus('pending');
        try {
            const data = await fetchWhatsappQr(instanceId, configKey, sessionIdRef.current);
            if (!mountedRef.current) return;
            const qr = typeof data === 'string' ? data : data?.qr;
            if (qr) setQrCode(qr);
        } catch (e) {
            if (!mountedRef.current) return;
            setSaveError(e.message || 'Failed to load QR code');
            setConnectionStatus('save_failed');
            return;
        }
        statusIntervalRef.current = setInterval(pollStatus, 3000);
    }, [instanceId, configKey, pollStatus, cleanup]);

    const startPairing = useCallback(async () => {
        cleanup();
        setSaveError('');
        setConnectionStatus('pairing');
        try {
            const data = await fetchWhatsappPairingCode(instanceId, configKey, sessionIdRef.current, phone.replace(/\D/g, ''));
            if (!mountedRef.current) return;
            const code = typeof data === 'string' ? data : (data?.code || data?.pairingCode);
            if (code) setPairingCode(code);
        } catch (e) {
            if (!mountedRef.current) return;
            setSaveError(e.message || 'Failed to get pairing code');
            setConnectionStatus('save_failed');
            return;
        }
        statusIntervalRef.current = setInterval(pollStatus, 3000);
    }, [instanceId, configKey, phone, pollStatus, cleanup]);

    const handleSubmit = () => {
        if (connectionStatus !== '') return;
        if (method === 'phone') startPairing();
        else startQr();
    };

    const phoneDigits = phone.replace(/\D/g, '');
    const editable = connectionStatus === '';
    const canSubmit = editable && topic.trim().length > 0 && (method !== 'phone' || phoneDigits.length >= 6);
    const showCollection = mode !== 'external' && collections.length > 0;

    return (
        <ChannelSetupLayout
            title="Configure WhatsApp Instance"
            isDashboard
            onBack={onBack}
            onCancel={onCancel}
            onSubmit={handleSubmit}
            submitDisabled={!canSubmit}
            loading={connectionStatus === 'saving'}
            submitLabel={method === 'phone' ? 'Get Code' : 'Generate QR'}
        >
            <div className="flex flex-col sm:flex-row w-full">
                {/* Config sidebar */}
                <aside className="sm:w-[360px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-0">
                    <div className="mb-6 space-y-1">
                        <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                            <MessageCircle className="h-3.5 w-3.5" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">Channel Configuration</span>
                        </div>
                        <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
                    </div>

                    <div className="flex flex-col">
                        <WaStep num="01">
                            <Label className={SIDEBAR_LABEL}>Instance Name <span className="text-destructive">*</span></Label>
                            <Input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Support" disabled={!editable} className="h-9 text-xs" />
                        </WaStep>

                        <WaStep num="02">
                            <Label className={SIDEBAR_LABEL}>Channel Mode <span className="text-destructive">*</span></Label>
                            <NewCombobox
                                className="mt-0.5"
                                comboboxWidth="w-full"
                                defaultDisplayed="Select Mode"
                                selectedValue={mode}
                                setter={(v) => setMode(v)}
                                disabled={!editable}
                                noSearch
                                items={[
                                    {
                                        value: 'conversation',
                                        name: 'Conversation'
                                    },
                                    {
                                        value: 'receive_only',
                                        name: 'Receive Only'
                                    },
                                    {
                                        value: 'external',
                                        name: 'Send Only (API)'
                                    },
                                ]}
                            />
                            <p className="text-[10px] text-muted-foreground mt-1 italic">{WA_MODE_HINT[mode]}</p>
                        </WaStep>

                        <WaStep num="03" connector={showCollection}>
                            <Label className={SIDEBAR_LABEL}>Auth Method <span className="text-destructive">*</span></Label>
                            <NewCombobox
                                className="mt-0.5"
                                comboboxWidth="w-full"
                                defaultDisplayed="Select Method"
                                selectedValue={method}
                                setter={(v) => setMethod(v)}
                                disabled={!editable}
                                noSearch
                                items={[
                                    {
                                        value: 'qr',
                                        name: 'QR Code'
                                    },
                                    {
                                        value: 'phone',
                                        name: 'Phone Number (Code)'
                                    },
                                ]}
                            />
                            {method === 'phone' && (
                                <div className="mt-2">
                                    <Label className={SIDEBAR_LABEL}>Phone Number <span className="text-destructive">*</span></Label>
                                    <Input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="60123456789" disabled={!editable} className="h-9 text-xs" />
                                    <p className="text-[10px] text-muted-foreground mt-1 italic">Digits only, including country code.</p>
                                </div>
                            )}
                        </WaStep>

                        {showCollection && (
                            <WaStep num="04" connector={false}>
                                <Label className={SIDEBAR_LABEL}>Archive to KB Collection</Label>
                                <NewCombobox
                                    className="mt-0.5"
                                    comboboxWidth="w-full"
                                    defaultDisplayed="None"
                                    selectedValue={collectionId}
                                    setter={(v) => setCollectionId(v)}
                                    disabled={!editable}
                                    items={[
                                        {
                                            value: '',
                                            name: 'None'
                                        },
                                        ...collections.map(c => ({
                                            value: c._id,
                                            name: c.name || c.title || c._id
                                        })),
                                    ]}
                                />
                                <p className="text-[10px] text-muted-foreground mt-1 italic">Optional — store incoming messages in a knowledge collection.</p>
                            </WaStep>
                        )}
                    </div>
                </aside>

                {/* Connection panel */}
                <main className="flex-grow bg-background p-8 flex flex-col items-center justify-center relative">
                    <div className="w-full max-w-sm space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                        <div className="text-center flex flex-col items-center gap-6">
                            <BaileysConnectionPanel
                                connectionStatus={connectionStatus}
                                qrCode={qrCode}
                                saveError={saveError}
                                pairingCode={pairingCode}
                                hideAssistant
                            />
                        </div>
                    </div>
                </main>
            </div>
        </ChannelSetupLayout>
    );
}

function WhatsAppChannelEdit({
    channel, instanceId, configKey, onDisconnected, onCancel
}) {
    const [disconnecting, setDisconnecting] = useState(false);
    const [error, setError] = useState(null);

    const handleDisconnect = async () => {
        setDisconnecting(true);
        setError(null);
        try {
            await disconnectWhatsapp(instanceId, configKey, channel.address, channel._id);
            onDisconnected(channel._id);
        } catch (e) {
            setError(e.message || 'Failed to disconnect');
            setDisconnecting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-600 flex items-center justify-center text-white shrink-0">
                    <MessageCircle className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-medium">Connected Number</p>
                    <p className="text-sm text-muted-foreground font-mono truncate">{channel.address}</p>
                </div>
                <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-green-600 shrink-0">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    Connected
                </span>
            </div>

            {channel.topic && (
                <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Topic</p>
                    <p className="text-sm">{channel.topic}</p>
                </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-2 pt-1">
                <Button
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                >
                    <Power className="h-4 w-4" />
                    {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
                <Button variant="outline" size="sm" onClick={onCancel}>Close</Button>
            </div>
        </div>
    );
}

function WebchatPreviewDialog({
    open, onClose, channel
}) {
    const modalRef = useRef(null);
    if (!channel) return null;

    const chatSettings = {
        primaryColor: channel.webChatConfig?.primaryColor || '#FFFFFF',
        secondaryColor: channel.webChatConfig?.secondaryColor || '#000000',
        textPrimaryColor: channel.webChatConfig?.textPrimaryColor || '#000000',
        textSecondaryColor: channel.webChatConfig?.textSecondaryColor || '#FFFFFF',
        tertiaryColor: channel.webChatConfig?.tertiaryColor || '#FFFFFF',
        textTertiaryColor: channel.webChatConfig?.textTertiaryColor || '#000000',
        accentColor: channel.webChatConfig?.accentColor || '#000000',
        textAccentColor: channel.webChatConfig?.textAccentColor || '#FFFFFF',
        aiIconColor: channel.webChatConfig?.aiIconColor || '#000000',
        userIconColor: channel.webChatConfig?.userIconColor || '#FFFFFF',
        assistantName: channel.webChatConfig?.assistantName || 'BeX AI Assistant',
        chatGreetingMessage: channel.webChatConfig?.chatGreetingMessage || channel.webChatConfig?.greetingMessage || '',
        chatWidth: channel.webChatConfig?.chatWidth || 380,
        chatHeight: channel.webChatConfig?.chatHeight || 500,
        conversationStarters: channel.webChatConfig?.conversationStarters || [],
    };

    const emailUpdateObject = {
        _id: channel._id,
        address: channel.address,
        includeConvoHistory: channel.includeConvoHistory || false,
        webChatConfig: channel.webChatConfig,
        organizationId: '',
        subOrganizationId: '',
        topic: channel.topic || '',
        type: channel.type,
        assistantId: channel.assistantId || '',
    };

    return (
        <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
            <DialogContent className="p-0 sm:max-w-[1200px] h-[80vh]" ref={modalRef}>
                <DialogHeader className="px-6 pt-6 pb-0">
                    <DialogTitle>Web Chat Preview</DialogTitle>
                </DialogHeader>
                <div className="relative flex-1 overflow-hidden h-full">
                    <WebchatWidget
                        chatSettings={chatSettings}
                        emailUpdateObject={emailUpdateObject}
                        modalRef={modalRef}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AddChannelDialog({
    open, onSave, onClose, instanceId, configKey, assistantId, collections, onChannelCreated, organizationId
}) {
    const [step, setStep] = useState('select');
    const [selectedType, setSelectedType] = useState(null);
    const typeInfo = CHANNEL_TYPES.find(t => t.id === selectedType);

    const reset = () => { setStep('select'); setSelectedType(null); };
    const handleOpenChange = (v) => { if (!v) { onClose(); reset(); } };
    const handleSave = async (payload) => { await onSave(payload); onClose(); reset(); };
    const closeAll = () => { onClose(); reset(); };

    // WhatsApp uses the wide channel-adder layout (sidebar + connection panel),
    // which provides its own header + footer, so we render it edge-to-edge.
    const isWa = step === 'form' && selectedType === 'Whatsapp';

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className={isWa ? 'p-0 gap-0 sm:max-w-[900px] overflow-hidden' : 'sm:max-w-lg'}>
                {isWa ? (
                    <>
                        <DialogTitle className="sr-only">New WhatsApp Channel</DialogTitle>
                        <WhatsAppQrFlow
                            instanceId={instanceId}
                            configKey={configKey}
                            assistantId={assistantId}
                            collections={collections}
                            onChannelCreated={(ch) => { onChannelCreated(ch); closeAll(); }}
                            onBack={reset}
                            onCancel={closeAll}
                        />
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            {step === 'select' ? (
                                <>
                                    <DialogTitle>Select Channel Type</DialogTitle>
                                    <p className="text-sm text-muted-foreground">Choose a channel type to get started connecting your enterprise.</p>
                                </>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Button variant="ghost" size="sm" className="h-7 px-2 gap-1 text-xs" onClick={reset}>
                                        <ChevronLeft className="h-3 w-3" /> Back
                                    </Button>
                                    <DialogTitle>New {typeInfo?.label} Channel</DialogTitle>
                                </div>
                            )}
                        </DialogHeader>

                        {step === 'select' && (
                            <div className="grid grid-cols-3 gap-3 pt-1">
                                {CHANNEL_TYPES.map(t => {
                                    const Icon = t.icon;
                                    return (
                                        <button key={t.id} onClick={() => { setSelectedType(t.id); setStep('form'); }}
                                            className="flex flex-col items-start gap-3 rounded-xl border p-4 hover:border-primary hover:bg-accent transition-colors cursor-pointer text-left">
                                            <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center text-white', t.iconBg)}>
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold">{t.label}</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {step === 'form' && selectedType !== 'Whatsapp' && (
                            <div className="max-h-[65vh] overflow-y-auto pr-1">
                                <ChannelForm type={selectedType} onSave={handleSave} onCancel={reset} saveLabel="Create Channel" cancelLabel="Back" organizationId={organizationId} />
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function ChannelConfig({
    channels: initialChannels, assistants = [], collections = [], instanceId, configKey, organizationId
}) {
    const [channels, setChannels] = useState(initialChannels || []);
    const [editingId, setEditingId] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [previewCh, setPreviewCh] = useState(null);
    const [success, setSuccess] = useState(null);

    const assistantId = assistants[0]?.assistant_id || assistants[0]?._id || '';
    const showSuccess = (id) => { setSuccess(id); setTimeout(() => setSuccess(null), 3000); };

    const handleUpdate = async (channelId, payload) => {
        await updateChannel(instanceId, configKey, channelId, {
            ...payload,
            assistantId
        });
        setChannels(prev => prev.map(ch => ch._id === channelId ? {
            ...ch,
            ...payload,
            assistantId
        } : ch));
        setEditingId(null); showSuccess(channelId);
    };

    const handleCreate = async (payload) => {
        const created = await createChannel(instanceId, configKey, {
            ...payload,
            assistantId
        });
        setChannels(prev => [...prev, created]); showSuccess(created._id);
    };

    const handleChannelCreated = (channel) => {
        setChannels(prev => [...prev, channel]);
        showSuccess(channel._id);
    };

    return (
        <div className="space-y-3 max-w-2xl">
            <div className="flex justify-end">
                <Button onClick={() => setShowDialog(true)}>+ Add Channel</Button>
            </div>
            <AddChannelDialog
                open={showDialog}
                onSave={handleCreate}
                onClose={() => setShowDialog(false)}
                instanceId={instanceId}
                configKey={configKey}
                assistantId={assistantId}
                collections={collections}
                onChannelCreated={handleChannelCreated}
                organizationId={organizationId}
            />
            <WebchatPreviewDialog
                open={!!previewCh}
                onClose={() => setPreviewCh(null)}
                channel={previewCh}
            />
            {channels.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="h-14 w-14 rounded-full bg-[#b1092a]/10 flex items-center justify-center mb-4">
                        <MessageSquare className="h-7 w-7 text-[#b1092a]" />
                    </div>
                    <h3 className="text-base font-semibold mb-1">No channels configured</h3>
                    <p className="text-sm text-muted-foreground max-w-sm mb-4">
                        Channels connect your assistant to communication platforms like Web Chat and WhatsApp.
                    </p>
                    <Button onClick={() => setShowDialog(true)}>+ Add Your First Channel</Button>
                </div>
            )}
            {channels.map(ch => (
                <Card key={ch._id}>
                    <CardHeader className="py-3 px-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium text-sm truncate">{ch.address || ch._id}</span>
                                <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full shrink-0', TYPE_BADGE[ch.type] || 'bg-muted text-muted-foreground')}>{ch.type}</span>
                                {ch.topic && <span className="text-xs text-muted-foreground truncate hidden sm:block">{ch.topic}</span>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {success === ch._id && <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3.5 w-3.5" /> Saved</span>}
                                {ch.type === 'Webchat' && (
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setPreviewCh(ch)} title="Preview">
                                        <Eye className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingId(editingId === ch._id ? null : ch._id)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    {editingId === ch._id && (
                        <CardContent className="border-t pt-4">
                            {ch.type === 'Whatsapp' ? (
                                <WhatsAppChannelEdit
                                    channel={ch}
                                    instanceId={instanceId}
                                    configKey={configKey}
                                    onDisconnected={(id) => {
                                        setChannels(prev => prev.filter(c => c._id !== id));
                                        setEditingId(null);
                                    }}
                                    onCancel={() => setEditingId(null)}
                                />
                            ) : (
                                <ChannelForm type={ch.type} initialData={fromChannel(ch)} onSave={p => handleUpdate(ch._id, p)} onCancel={() => setEditingId(null)} organizationId={organizationId} />
                            )}
                        </CardContent>
                    )}
                </Card>
            ))}
        </div>
    );
}
